import { z } from 'zod';

const id = z.string().min(1).max(160);
const coordinate = z.number().finite().min(-100000).max(100000);
const dimension = z.number().finite().positive().max(8192);
const color = z.string().max(80);
/** How far one turn may go: quick, standard and thorough are chat turns (8, 16, 24 steps); long is a task (200 steps, two hours). */
export const CreativeEffortSchema = z.enum(['quick', 'standard', 'thorough', 'long']);
export type CreativeEffort = z.infer<typeof CreativeEffortSchema>;

export const CreativeLayerSchema = z.object({
  id: id,
  type: z.enum(['text', 'image', 'rect', 'circle']),
  text: z.string().max(20000).optional(),
  url: z.string().max(4096).optional(),
  x: coordinate.optional(), y: coordinate.optional(),
  width: dimension.optional(), height: dimension.optional(),
  fill: color.optional(), fontSize: z.number().positive().max(1200).optional(),
  fontFamily: z.string().max(160).optional(), bold: z.boolean().optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  lineHeight: z.number().min(0.5).max(4).optional(),
  radius: z.number().min(0).max(8192).optional(),
  angle: z.number().min(-360).max(360).optional(),
  opacity: z.number().min(0).max(1).optional(),
  fit: z.enum(['contain', 'cover']).optional(),
}).strict();

export const CreativeDesignSchema = z.object({
  width: dimension, height: dimension, background: color,
  layers: z.array(CreativeLayerSchema).min(1).max(100),
}).strict().superRefine((spec, ctx) => {
  const ids = new Set<string>();
  spec.layers.forEach((layer, index) => {
    if (ids.has(layer.id)) ctx.addIssue({ code: 'custom', path: ['layers', index, 'id'], message: 'Layer IDs must be unique' });
    ids.add(layer.id);
    if (layer.type === 'text' && !layer.text?.trim()) ctx.addIssue({ code: 'custom', path: ['layers', index, 'text'], message: 'Text is required' });
    if (layer.type === 'image' && !layer.url) ctx.addIssue({ code: 'custom', path: ['layers', index, 'url'], message: 'Image reference is required' });
  });
});

export const CreativeChangeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('update'), artboardId: id, expectedRevision: id,
    patch: z.object({ title: z.string().min(1).max(300).optional(), x: coordinate.optional(), y: coordinate.optional(),
      w: dimension.optional(), h: dimension.optional(), text: z.string().max(20000).optional(),
    }).strict(),
  }).strict(),
  z.object({ type: z.literal('design'), artboardId: id, expectedRevision: id, spec: CreativeDesignSchema }).strict(),
  z.object({ type: z.literal('create_design'), title: z.string().min(1).max(300), x: coordinate, y: coordinate, spec: CreativeDesignSchema }).strict(),
  /** An image frame holding one collected or generated file, sized from its own pixels. */
  z.object({ type: z.literal('create_image'), title: z.string().min(1).max(300), x: coordinate, y: coordinate, url: z.string().url() }).strict(),
  z.object({ type: z.literal('create_note'), title: z.string().min(1).max(300), text: z.string().max(20000), x: coordinate, y: coordinate }).strict(),
  z.object({ type: z.literal('create_section'), title: z.string().min(1).max(300), x: coordinate, y: coordinate, w: dimension, h: dimension }).strict(),
  z.object({ type: z.literal('delete'), artboardId: id, expectedRevision: id }).strict(),
]);
export const CreativeChangesSchema = z.object({ changes: z.array(CreativeChangeSchema).min(1).max(24) }).strict();
export const CreativeInspectSchema = z.object({ artboardIds: z.array(id).max(20) }).strict();
export const CreativeSkillSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  title: z.string().min(1).max(100),
  description: z.string().min(1).max(500),
  instructions: z.string().min(1).max(20000),
  requiredTools: z.array(z.string().min(1).max(100)).max(30),
  provenance: z.object({ author: z.string(), license: z.string(), origin: z.string() }).strict(),
  evaluations: z.array(z.string().min(1)).min(1).max(20),
}).strict();
export const CreativeSkillDiscoverySchema = z.object({ query: z.string().max(500).optional() }).strict();
export type CreativeSkill = z.infer<typeof CreativeSkillSchema>;
export const CreativeProductionSchema = z.object({
  template: z.enum(['product_launch', 'property_listing', 'client_presentation']),
  title: z.string().trim().min(1).max(120),
  variant: z.string().trim().max(80).optional(),
  pages: z.array(z.object({
    heading: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(320),
    imageArtboardId: id.optional(),
  }).strict()).min(3).max(12),
}).strict();
export const CreativeProductionInspectSchema = z.object({ productionId: id }).strict();
export const CreativeProductionListSchema = z.object({}).strict();
export const CreativeProductionTemplateSchema = z.object({
  id: CreativeProductionSchema.shape.template, version: z.string(), title: z.string(), skill: z.string(),
  pages: z.number().int().positive().optional(),
  dimensions: z.array(z.tuple([dimension, dimension])), outputs: z.array(z.string()),
}).strict();
export type CreativeProductionTemplate = z.infer<typeof CreativeProductionTemplateSchema>;
export const CreativeRecipeSchema = z.object({ recipe: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/) }).strict();
/** Choices may include context while existing saved string choices remain valid. */
export const CreativeAskChoiceSchema = z.union([
  z.string().trim().min(1).max(60),
  z.object({
    label: z.string().trim().min(1).max(60),
    description: z.string().trim().max(200).optional(),
    recommended: z.boolean().optional(),
  }).strict(),
]);
/** ask_choices: a short questionnaire, shown one question at a time. */
export const CreativeAskQuestionSchema = z.object({
  question: z.string().trim().min(1).max(200),
  choices: z.array(CreativeAskChoiceSchema).min(2).max(6),
  multiSelect: z.boolean().default(false),
}).strict();
export const CreativeAskSchema = z.object({ questions: z.array(CreativeAskQuestionSchema).min(1).max(4) }).strict();
/** run_tool: one studio tool (the canvas-app catalog) applied to a frame; list_tools describes them. */
export const CreativeToolValueSchema = z.union([z.string().max(12000), z.number(), z.boolean()]);
export const CreativeRunToolSchema = z.object({
  tool: z.string().trim().min(1).max(120),
  frameId: id,
  values: z.record(z.string(), CreativeToolValueSchema).default({}),
  inputs: z.record(z.string(), id).default({}),
  /** How to run it: an option id (or its label) the user picked from the run options. Without one the tool asks. */
  route: z.string().trim().min(1).max(200).optional(),
}).strict();
export const CreativeListToolsSchema = z.object({}).strict();
/**
 * A flag on a frame (`params.flags`): one an inspect wrote (bg, cut, tone, ground, foot, lost) or one set by hand or a
 * script for a zone's rule to read (water). In find_frames, `any` means carrying any flag.
 */
export const FrameFlagSchema = z.string().regex(/^[\w-]{1,40}$/);
/**
 * find_frames: the frames of this canvas that match, compactly: inside a section or a Compositor, by words in the
 * title, by kind, locked or not, by a flag they carry, and by values under params.meta (`where: { panel: 'Hell' }`).
 */
export const CreativeFindFramesSchema = z.object({
  sectionId: id.optional(),
  title: z.string().trim().min(1).max(200).optional(),
  kind: z.enum(['media', 'note', 'section', 'composition', 'design', 'web', 'tool']).optional(),
  locked: z.boolean().optional(),
  flagged: FrameFlagSchema.optional(),
  where: z.record(z.string().min(1).max(80), z.union([z.string().max(200), z.number(), z.boolean()])).optional(),
  limit: z.number().int().min(1).max(1000).default(200),
}).strict();
/**
 * run_tool_batch: one studio tool over a set of frames, as one job. The frames are the given ids, the members of a
 * section, or the frames carrying a flag (with a section, only those inside it); locked frames are skipped and named.
 * The run route is decided once for the batch. `sheet` lays each changed frame's earlier version beside its new one.
 */
export const CreativeRunToolBatchSchema = z.object({
  tool: z.string().trim().min(1).max(120),
  frameIds: z.array(id).min(1).max(1000).optional(),
  sectionId: id.optional(),
  flagged: FrameFlagSchema.optional(),
  values: z.record(z.string(), CreativeToolValueSchema).default({}),
  inputs: z.record(z.string(), id).default({}),
  route: z.string().trim().min(1).max(200).optional(),
  sheet: z.boolean().default(true),
  /** A new version that keeps under a third of the picture under it: hold it back (it stays in the versions, the earlier one shows) or take it. */
  lost: z.enum(['hold', 'take']).default('hold'),
}).strict();
/** A contact sheet of frames: their pictures on one labelled grid, or each one's earlier version beside its current one. */
export const CreativeContactSheetSchema = z.object({
  frameIds: z.array(id).min(1).max(64),
  pairs: z.boolean().default(false),
  before: z.enum(['previous', 'first']).default('previous'),
  columns: z.number().int().min(1).max(8).optional(),
  title: z.string().trim().min(1).max(120).optional(),
}).strict();
/** An audit of frames: which to measure (ids, a section, or the canvas), which checks, whether the flags are written on them. */
export const CreativeInspectFramesSchema = z.object({
  frameIds: z.array(id).max(1000).optional(),
  sectionId: id.optional(),
  checks: z.array(z.enum(['bg', 'cut', 'tone', 'ground', 'foot', 'lost'])).min(1).optional(),
  write: z.boolean().default(true),
}).strict();
/** The ground of a big picture as a class map: prompts per class over tiles, merged into one field kept on the frame. */
export const CreativeClassMapSchema = z.object({
  frameId: id,
  /** Class name to its prompts; default water, trees (tree, bush, hedge) and solid (rock, building, tower); the rest is ground. */
  groups: z.record(z.string().min(1).max(40), z.array(z.string().min(1).max(60)).min(1).max(8)).optional(),
  tile: z.number().int().min(256).max(4096).default(1400),
  step: z.number().int().min(128).max(4096).default(1200),
  width: z.number().int().min(512).max(16384).default(8192),
  /** Only the tiles touching this box, as fractions of the picture (0..1). */
  box: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().positive().max(1), h: z.number().positive().max(1) }).strict().optional(),
  engine: z.enum(['cloud', 'local']).default('cloud'),
  preview: z.boolean().default(true),
}).strict();
const zoneCode = z.string().regex(/^\d{1,3}$/);
/**
 * A frame's zones (its class map): read them, or change their names and rules. The rule's shape is the
 * canonical `FieldClassRuleSchema` of `@genieartinc/schemas/graph`, which the engine checks it against.
 */
export const CreativeZonesSchema = z.object({
  frameId: id,
  /** Zone code to its name; the zones not named keep theirs. */
  legend: z.record(zoneCode, z.string().min(1).max(200)).optional(),
  /** Zone code to its rule: who may stand on it (all, none, or flagged with the flag), whether frames keep off it, its colour. */
  rules: z.record(zoneCode, z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    feet: z.enum(['all', 'none', 'flagged']).optional(),
    flag: z.string().regex(/^[\w-]{1,40}$/).optional(),
    keepClear: z.boolean().optional(),
    note: z.string().max(300).optional(),
  }).strict()).optional(),
}).strict();
/**
 * A deep-zoom export: a Compositor's scene (or a section of the Board) as a folder for the web: a tile pyramid, a
 * viewer with no dependencies, and a page per item that opens on the whole picture and flies to it.
 */
export const CreativeDeepZoomSchema = z.object({
  compositionId: id.optional(),
  sectionId: id.optional(),
  /** Level 0's width in pixels (default the scene at 1:1, at most 65536). */
  width: z.number().int().min(256).max(65536).optional(),
  /** WebP quality of the tiles (default 84). */
  quality: z.number().int().min(40).max(100).optional(),
  /** Also a 1600 px square still round each item and one of the whole picture. */
  stills: z.boolean().optional(),
  /** The index page's title (default the frame's). */
  title: z.string().min(1).max(200).optional(),
  /** The key of params.meta that names an item's page (default `n`, else its place) and its group line (default `panel`). */
  itemKey: z.string().min(1).max(40).optional(),
  groupKey: z.string().min(1).max(40).optional(),
}).strict();

/**
 * Placement by a ground's zones: find the frames standing on it that break its zones' rules (a foot on a zone that
 * does not take it, paint over a zone kept clear) and the nearest spot that keeps each (`propose`, nothing moves);
 * move them (`apply`); put them back (`revert`).
 */
export const CreativePlaceByZonesSchema = z.object({
  action: z.enum(['propose', 'apply', 'revert']),
  /** propose: the frame with the zones (the ground); the frames standing on it are those beside it over its box. */
  frameId: id.optional(),
  /** propose: check only these frames; apply or revert: only these frames of the plan. */
  frameIds: z.array(id).min(1).max(2000).optional(),
  /** apply, revert: the plan's id, from propose. */
  planId: z.string().regex(/^[a-f0-9]{8}$/).optional(),
  /** propose: the key of params.meta that names a frame's group (default `panel`): a frame moves only within its group's stretch. */
  groupKey: z.string().min(1).max(40).optional(),
  /** propose: the groups' stretches left to right, as shares of the ground's width (default: between each group's frames). */
  panels: z.array(z.object({ name: z.string().min(1).max(40), from: z.number().min(0).max(1), to: z.number().min(0).max(1) }).strict()).min(1).max(12).optional(),
  /** propose: how far a frame looks before it is tried across its group, as shares of the ground's width. */
  reachX: z.number().min(0.001).max(1).optional(),
  reachY: z.number().min(0.001).max(1).optional(),
  /** propose: a frame breaks the rules under badFoot of its foot on allowed zones or over badCover of its paint on kept-clear ones; a new spot needs okFoot, at most maxCover and maxOverlap (paint over other frames), and a size within maxPersp of the depth there. */
  badFoot: z.number().min(0).max(1).optional(),
  badCover: z.number().min(0).max(1).optional(),
  okFoot: z.number().min(0).max(1).optional(),
  maxCover: z.number().min(0).max(1).optional(),
  maxOverlap: z.number().min(0).max(1).optional(),
  maxPersp: z.number().min(0).max(1).optional(),
  /** propose: the width the zones are read at (default 2048; finer is slower). */
  fieldWidth: z.number().int().min(256).max(8192).optional(),
}).strict();

/** Frames placed on their ground by rules: a Compositor's layers, a section's members or given frames; panels, the meta keys the order and groups read, the sizes; a dry run and a study. */
export const CreativeArrangeFramesSchema = z.object({
  /** The Compositor whose layers are placed (their ground is its layer with the class map). */
  compositionId: id.optional(),
  /** A section on the Board: the frames inside its box. */
  sectionId: id.optional(),
  /** Only these frames move; the others stand as walls. */
  frameIds: z.array(id).min(1).max(1000).optional(),
  /** Stretches of the ground left to right, filled by count in the order of the score (default one). `factor` starts the panel's scale. */
  panels: z.array(z.object({ name: z.string().min(1).max(40), from: z.number().min(0).max(1), to: z.number().min(0).max(1), factor: z.number().min(0.3).max(1.5).optional() }).strict()).min(1).max(12).optional(),
  /** Which keys of params.meta give the order (default `score`, else the present left-to-right place), the groups (`cluster`) and the twins (`topic`). */
  scoreKey: z.string().min(1).max(40).optional(),
  clusterKey: z.string().min(1).max(40).optional(),
  twinKey: z.string().min(1).max(40).optional(),
  /** The horizon as a fraction of the ground's height; above it is sky (default 0.21). */
  horizon: z.number().min(0).max(0.9).optional(),
  /** The width of a frame standing at the horizon and at the front: fractions of the ground's width (up to 1) or its units. */
  far: z.number().positive().optional(),
  near: z.number().positive().optional(),
  seed: z.number().int().min(0).optional(),
  /** Report the plan without moving anything. */
  dryRun: z.boolean().default(false),
  /** Land the plan drawn on the ground as a frame beside it. */
  study: z.boolean().default(false),
}).strict();
/** A rectangle of the canvas as one picture: a section's box, the bounding box of frames, or a box in canvas units; the size in pixels; where it goes. */
export const CreativeExportRegionSchema = z.object({
  /** A Compositor frame: the region is then in its scene's units (the whole scene when nothing else is given). */
  compositionId: id.optional(),
  sectionId: id.optional(),
  frameIds: z.array(id).min(1).max(1000).optional(),
  box: z.object({ x: z.number(), y: z.number(), w: z.number().positive(), h: z.number().positive() }).strict().optional(),
  /** Draw only the named frames (grounds included), not everything that stands in their box. */
  only: z.boolean().default(false),
  height: z.number().int().min(16).max(16384).optional(),
  width: z.number().int().min(16).max(16384).optional(),
  format: z.enum(['png', 'jpg', 'webp']).default('png'),
  quality: z.number().int().min(40).max(100).default(90),
  /** A flat colour under the picture (jpg and webp flatten on the Board's colour without one). */
  background: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  as: z.enum(['frame', 'file']).default('frame'),
  title: z.string().trim().min(1).max(120).optional(),
}).strict();
/** A graph to run: a saved one by id, or a built-in pass over a section (the notes may run over the whole canvas). */
export const CreativeRunGraphSchema = z.object({
  graphId: id.optional(),
  recipe: z.enum(['notes', 'blend', 'night', 'inspect']).optional(),
  /** A section (the Board frames inside it) or a Compositor (its layers). */
  sectionId: id.optional(),
  /** The frames to work on; with sectionId, only those of it. */
  frameIds: z.array(id).min(1).max(1000).optional(),
  overrides: z.record(z.string(), z.record(z.string(), z.union([z.string().max(12000), z.number(), z.boolean()]))).optional(),
  wait: z.boolean().default(true),
}).strict();
export type CreativeAskQuestion = z.infer<typeof CreativeAskQuestionSchema>;
/** The selected output mode is independent of the subject's visual style. */
export const CreativeModeSchema = z.enum(['director', 'image', 'video', 'sound', '3d', 'code']);
export type CreativeMode = z.infer<typeof CreativeModeSchema>;
export const CreativeGenerateMeshSchema = z.object({
  title: z.string().min(1).max(300), prompt: z.string().min(1).max(12000),
  sourceArtboardId: id.optional(), sourceImage: z.string().url().max(4096).optional(),
}).strict();
export const CreativeGenerateSchema = z.object({
  images: z.array(z.object({ title: z.string().min(1).max(300), prompt: z.string().min(1).max(12000),
    width: z.number().int().min(256).max(2048), height: z.number().int().min(256).max(2048),
    sourceArtboardId: id.optional(), sourceImage: z.string().url().max(4096).optional(),
  }).strict()).min(1).max(8),
}).strict();
export const CreativeJobsSchema = z.object({ jobIds: z.array(id).min(1).max(8), wait: z.boolean() }).strict();

/** The money ledger of a run: what was spent, the cap, what the cache saved, the calls. */
export const CreativeLedgerSchema = z.object({
  /** A run to read; the agent's own run when left out. */
  runId: z.string().trim().min(1).max(200).optional(),
}).strict()
export type CreativeLedger = z.infer<typeof CreativeLedgerSchema>

export const CreativeProviderSchema = z.enum(['openai', 'anthropic', 'xai', 'openrouter', 'ollama']);
/** Versioned agent inference contract. Unknown models reject overrides at the gateway. */
export const CreativeInferenceSchema = z.object({
  maxOutputTokens: z.number().int().min(256).max(6000).optional(),
  temperature: z.number().min(0).max(2).optional(),
  topP: z.number().min(0).max(1).optional(),
}).strict();
export const LocalAgentModelRequestSchema = z.object({ model: z.string().min(1).max(200) }).strict();
export const LocalAgentModelJobSchema = z.object({
  model: z.string().min(1).max(200),
  status: z.enum(['downloading', 'verifying', 'interrupted', 'ready', 'cancelled', 'error']),
  message: z.string().max(300), completed: z.number().nonnegative().optional(), total: z.number().nonnegative().optional(),
});
export const LocalAgentModelCatalogSchema = z.object({
  available: z.boolean(), memoryBytes: z.number().positive(), job: LocalAgentModelJobSchema.nullable(),
  models: z.array(z.object({
    id: z.string().min(1).max(200), name: z.string(), source: z.string(),
    downloadBytes: z.number().nonnegative(), installed: z.boolean(), downloadable: z.boolean(), cloud: z.boolean(),
    supportsImages: z.boolean(), tools: z.enum(['advertised', 'unverified']), memoryRequirement: z.string(),
  })),
});
export type LocalAgentModelJob = z.infer<typeof LocalAgentModelJobSchema>;
export type LocalAgentModelCatalog = z.infer<typeof LocalAgentModelCatalogSchema>;
export const CreativeConnectionSchema = z.object({
  id, name: z.string().min(1).max(80), provider: CreativeProviderSchema,
  model: z.string().min(1).max(200),
  baseUrl: z.string().url().max(500).optional(),
  supportsImages: z.boolean().default(false),
  inference: CreativeInferenceSchema.optional(),
  /** `oauth`: the stored key is a provider access token the engine refreshes itself (Grok sign-in). */
  auth: z.enum(['key', 'oauth']).default('key'),
});
export const CreativeConnectionInputSchema = CreativeConnectionSchema.extend({ apiKey: z.string().max(1000).optional() }).strict();
export const CreativeSettingsInputSchema = z.object({
  connection: CreativeConnectionInputSchema.optional(), removeConnectionId: id.optional(), activeConnectionId: id.optional(),
  effort: CreativeEffortSchema.optional(),
  searchApiKey: z.string().max(1000).optional(),
}).strict();
/** Most text any single run can carry from attached documents (about 50k tokens). */
export const CREATIVE_ATTACHMENT_TEXT_LIMIT = 200000;
/**
 * A file the user attached in the composer. Documents travel as extracted text;
 * media is imported first and travels as the reference frame that holds it.
 */
export const CreativeAttachmentSchema = z.union([
  z.object({ kind: z.literal('document'), name: z.string().min(1).max(200), text: z.string().max(CREATIVE_ATTACHMENT_TEXT_LIMIT), truncated: z.boolean().default(false) }).strict(),
  z.object({ kind: z.enum(['image', 'video', 'audio', 'model']), name: z.string().min(1).max(200), artboardId: id }).strict(),
]);
export type CreativeAttachment = z.infer<typeof CreativeAttachmentSchema>;
export const CreativeRunRequestSchema = z.object({
  runId: id, sessionId: id, canvasId: id, text: z.string().min(1).max(20000),
  selectedIds: z.array(id).max(40).default([]),
  images: z.array(z.string().max(12000000)).max(8).default([]),
  attachments: z.array(CreativeAttachmentSchema).max(12).default([])
    .refine((files) => files.reduce((sum, file) => sum + ('text' in file ? file.text.length : 0), 0) <= CREATIVE_ATTACHMENT_TEXT_LIMIT, 'Attached documents are too long. Remove one and try again.'),
  connectionId: id.optional(),
  creationMode: CreativeModeSchema.optional(),
  effort: CreativeEffortSchema.default('standard'),
  preferences: z.object({ width: dimension.optional(), height: dimension.optional(), style: z.string().max(1000).optional(), count: z.number().int().min(1).max(8).optional() }).strict().optional(),
  media: z.object({ provider: z.enum(['local', 'fal', 'replicate', 'xai']).default('local'), model: z.string().max(200).optional(),
    maxImages: z.number().int().min(0).max(8).default(4),
  }).strict().default({ provider: 'local', maxImages: 4 }),
}).strict();
export const CreativeEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('started'), runId: id, sessionId: id }),
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({ type: z.literal('tool_start'), id, name: z.string(), input: z.record(z.string(), z.unknown()) }),
  z.object({ type: z.literal('tool_result'), id, name: z.string(), result: z.unknown(), failed: z.boolean().optional() }),
  /** A question for the person. `cap` marks the past-the-cap question: picking a raise sets that cap on the task's ledger run (the person's click, never the model's). */
  z.object({ type: z.literal('clarify'), id, questions: z.array(CreativeAskQuestionSchema).min(1).max(4),
    cap: z.object({ runId: id, raises: z.array(z.object({ label: z.string().max(60), capUsd: z.number().finite().min(0).max(10000) })).min(1).max(4) }).optional() }),
  /** Progress before the first token ("waiting for the local model…"); streamed, never stored. */
  z.object({ type: z.literal('status'), message: z.string().max(300) }),
  z.object({ type: z.literal('error'), message: z.string() }),
  z.object({ type: z.literal('finished'), status: z.enum(['completed', 'failed', 'cancelled', 'interrupted']), runId: id }),
]);
/**
 * A task: the work one request sets going, over as many turns as it takes (a question and its answer, a resume
 * after a restart). Its money is one ledger run; everything it changed on the canvas comes back with one undo.
 */
export const CreativeTaskSchema = z.object({
  id, sessionId: id, canvasId: id,
  status: z.enum(['running', 'awaiting', 'interrupted', 'done', 'failed', 'cancelled', 'undone']),
  effort: CreativeEffortSchema, runs: z.number().int().min(0), steps: z.number().int().min(0),
  startedAt: z.number(), updatedAt: z.number(), runningRunId: z.string().nullable(), lastRunId: z.string(), lastSeq: z.number().int().min(0),
  spentUsd: z.number(), pendingUsd: z.number(), capUsd: z.number(), frames: z.number().int().min(0), undoable: z.boolean(),
  error: z.string().nullable(),
});
export type CreativeTask = z.infer<typeof CreativeTaskSchema>;
export const CreativeTasksSchema = z.object({ tasks: z.array(CreativeTaskSchema) });
/** What an undo of a task put back: frames restored to how they were, frames it made taken away, and frames left alone because they changed since. */
export const CreativeTaskUndoSchema = z.object({
  taskId: id, restored: z.array(id), removed: z.array(id),
  skipped: z.array(z.object({ id, title: z.string(), reason: z.string() })),
});
export type CreativeTaskUndo = z.infer<typeof CreativeTaskUndoSchema>;
export type CreativeRunRequest = z.infer<typeof CreativeRunRequestSchema>;
export type CreativeEvent = z.infer<typeof CreativeEventSchema>;
export type CreativeConnection = z.infer<typeof CreativeConnectionSchema>;
export type CreativeSettingsInput = z.infer<typeof CreativeSettingsInputSchema>;
export type CreativeDesign = z.infer<typeof CreativeDesignSchema>;
export type CreativeChange = z.infer<typeof CreativeChangeSchema>;
export const CreativePublicSettingsSchema = z.object({
  searchConfigured: z.boolean().default(false),
  connections: z.array(CreativeConnectionSchema.extend({ configured: z.boolean() })),
  activeConnectionId: z.string(), effort: CreativeEffortSchema,
});
export const CreativeMessagePartSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({ type: z.literal('image'), image: z.string() }),
  z.object({ type: z.literal('tool-call'), toolCallId: id, toolName: z.string(),
    args: z.record(z.string(), z.unknown()), argsText: z.string(),
    result: z.unknown().optional(), isError: z.boolean().optional(),
  }),
]);
export const CreativeHistorySchema = z.object({
  messages: z.array(z.object({
    id: z.string(), runId: id, role: z.enum(['user', 'assistant']),
    content: z.array(CreativeMessagePartSchema),
  })),
});
export type CreativePublicSettings = z.infer<typeof CreativePublicSettingsSchema>;
export type CreativeHistory = z.infer<typeof CreativeHistorySchema>;

export const CreativeSearchSchema = z.object({
  query: z.string().trim().min(1).max(400),
  kind: z.enum(['web', 'images']),
  source: z.enum(['open', 'web']).default('open'),
  count: z.number().int().min(1).max(8).default(6),
}).strict();
export const CreativeCollectSchema = z.object({
  resultIds: z.array(id).min(1).max(6),
  title: z.string().min(1).max(200),
  x: coordinate, y: coordinate,
}).strict();
export const CreativeResearchItemSchema = z.object({
  id, title: z.string(), url: z.string().url(), description: z.string(),
  image: z.string().url().optional(), license: z.string(), creator: z.string(),
  licenseUrl: z.string().optional(), provider: z.string(),
});
export const CreativeResearchResultSchema = z.object({
  query: z.string(), provider: z.string(), results: z.array(CreativeResearchItemSchema),
});
export type CreativeResearchItem = z.infer<typeof CreativeResearchItemSchema>;
export const CreativeViewReferencesSchema = z.object({ images: z.array(z.string().url().max(4096)).min(1).max(4) }).strict();

export const CreativeComfyInspectSchema = z.object({
  kind: z.enum(['status', 'nodes', 'node', 'models']), query: z.string().max(200).default(''),
}).strict();
export const CreativeComfyWorkflowSchema = z.object({
  title: z.string().min(1).max(200),
  workflow: z.record(z.string().regex(/^\d{1,5}$/), z.object({
    class_type: z.string().min(1).max(100), inputs: z.record(z.string(), z.unknown()),
  }).strict()).refine((value) => Object.keys(value).length > 0 && Object.keys(value).length <= 40, 'Use 1–40 nodes'),
}).strict();
export const CreativeComfyCollectSchema = z.object({ jobId: id, title: z.string().min(1).max(200), x: coordinate, y: coordinate }).strict();

/**
 * render_3d_views: real camera renders of a 3D frame. One view is an orbit angle around the
 * model's bounding-sphere centre — azimuth 0 = front, 90 = right side (profile), 180 = back;
 * positive elevation looks down from above, negative is a low camera looking up.
 */
export const Creative3dViewSchema = z.object({
  title: z.string().trim().min(1).max(80).optional(),
  azimuth: z.number().finite().min(-360).max(360),
  elevation: z.number().finite().min(-89).max(89),
}).strict();
export const CreativeRender3dViewsSchema = z.object({
  sourceArtboardId: id,
  views: z.array(Creative3dViewSchema).min(1).max(8),
  width: z.number().int().min(256).max(1024).default(1024),
  height: z.number().int().min(256).max(1024).default(1024),
}).strict();
export type Creative3dView = z.infer<typeof Creative3dViewSchema>;
/** Engine → desktop window: render these views of a model offscreen and upload each one. */
export const CreativeThreeRenderRequestSchema = z.object({
  requestId: id, canvasId: id, artboardId: id,
  title: z.string().max(300),
  model: z.string().url().max(4096),
  /** The frame's saved viewer settings (params.three), applied where they change the object itself. */
  look: z.record(z.string(), z.unknown()).nullable(),
  views: z.array(Creative3dViewSchema.extend({ title: z.string().trim().min(1).max(80) })).min(1).max(8),
  width: z.number().int().min(256).max(1024),
  height: z.number().int().min(256).max(1024),
}).strict();
export const CreativeThreeRenderClaimSchema = z.object({ clientId: id }).strict();
export const CreativeThreeRenderViewSchema = z.object({
  clientId: id,
  dataUrl: z.string().max(24 * 1024 * 1024).regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/),
}).strict();
export const CreativeThreeRenderFailSchema = z.object({ clientId: id, error: z.string().trim().min(1).max(500) }).strict();
export const CreativeThreeRenderProgressSchema = z.object({ clientId: id, fraction: z.number().finite().min(0).max(1) }).strict();
export type CreativeThreeRenderRequest = z.infer<typeof CreativeThreeRenderRequestSchema>;

/** A canvas's conversations: which sessions carry messages, the one written to last first. */
export const CreativeSessionsSchema = z.object({
  sessions: z.array(z.object({ id: z.string(), createdAt: z.number(), messages: z.number().int().min(0), last: z.number().int().min(0) })),
});
export type CreativeSessions = z.infer<typeof CreativeSessionsSchema>;
/** A conversation held elsewhere, put into a session of a canvas so the chat shows it and later turns build on it. */
export const CreativeImportSchema = z.object({
  canvasId: id,
  sessionId: id.optional(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().trim().min(1).max(200000) })).min(1).max(2000),
}).strict();
export type CreativeImport = z.infer<typeof CreativeImportSchema>;
/** The documents attached to a canvas as its brief (the plan, the rules, the journal) that the agent reads as context. */
export const CanvasBriefSchema = z.object({
  documents: z.array(z.object({ name: z.string().trim().min(1).max(200), text: z.string().max(256 * 1024), updatedAt: z.number().optional() })).max(20),
}).strict();
export type CanvasBrief = z.infer<typeof CanvasBriefSchema>;
